import { useState } from "react";

/**
 * Filter mapping object - Maps filter names to CSS filter strings
 * 
 * This object defines all available camera filters with their corresponding
 * CSS filter properties. Each filter is carefully crafted to provide
 * specific visual effects for the camera feed.
 * 
 * Filter Categories:
 * - Basic: Normal, BW, Sepia
 * - Color Temperature: Warm, Cool, Yellowish, Greenish, Pink
 * - Artistic: Vintage, Soft, Vivid, Dramatic, Fade
 * - Special: ColdBW, BlurSoft, Invert
 * 
 * CSS Filter Properties Used:
 * - grayscale(): Converts to black and white (0-1)
 * - sepia(): Applies sepia tone (0-1)
 * - brightness(): Adjusts image brightness (<1 darker, >1 brighter)
 * - contrast(): Adjusts contrast (1 normal, >1 higher contrast)
 * - saturate(): Adjusts color saturation (0 grayscale, >1 more vibrant)
 * - hue-rotate(): Rotates color wheel (0-360deg)
 * - blur(): Applies Gaussian blur (length units)
 * - invert(): Inverts all colors (0-1)
 */
const filterMap = {
  // ===== BASIC FILTERS =====
  Normal: "none", // No filter applied - original camera feed

  BW: "grayscale(1) contrast(1.1)", // Black and white with enhanced contrast

  Sepia: "sepia(1)", // Classic sepia tone effect

  // ===== COLOR TEMPERATURE FILTERS =====
  Warm: "brightness(1.05) saturate(1.3) sepia(0.2)", // Warm tones with increased saturation

  Cool: "contrast(1.1) saturate(0.85) hue-rotate(350deg)", // Cool blue tones with reduced saturation

  Yellowish: "sepia(0.3) saturate(1.2) hue-rotate(20deg)", // Subtle yellow tint

  Greenish: "sepia(0.3) hue-rotate(90deg)", // Subtle green tint

  Pink: "sepia(0.2) saturate(1.4) hue-rotate(330deg)", // Pink/purple tones

  // ===== ARTISTIC FILTERS =====
  Vintage: "sepia(0.4) contrast(1.1) brightness(1.05)", // Vintage photo effect with warm tones

  Soft: "brightness(1.08) contrast(0.9) saturate(0.9)", // Soft, dreamy effect with reduced contrast

  Vivid: "saturate(1.6) contrast(1.15)", // Highly saturated and contrasted colors

  Dramatic: "contrast(1.3) brightness(0.95)", // High contrast, slightly darker for drama

  Fade: "contrast(0.85) brightness(1.1) saturate(0.8)", // Faded, retro look with reduced contrast

  // ===== SPECIAL EFFECTS =====
  ColdBW: "grayscale(1) contrast(1.2) brightness(0.95)", // High contrast black and white with cool tone

  BlurSoft: "blur(2px)", // Soft blur effect for dreamy appearance

  Invert: "invert(1)", // Color inversion effect (negative image)
};

/**
 * Custom hook for managing camera filter effects
 * 
 * This hook provides state management for applying real-time filters
 * to the camera feed. It maintains the currently active filter and
 * provides functions to update the filter selection.
 * 
 * Features:
 * - Manages active filter state with default to 'Normal'
 * - Provides access to complete filter mapping
 * - Simple API for filter selection and updates
 * - Integrates with CSS filter properties for real-time effects
 * 
 * Common Usage Patterns:
 * - Filter selection UI components
 * - Camera preview with real-time effects
 * - Image capture with applied filters
 * - Filter persistence across app sessions
 * 
 * @returns {Object} - Hook state and utility functions
 * @returns {string} returns.activeFilter - Currently active filter name (e.g., "Normal", "BW", "Vintage")
 * @returns {Function} returns.setActiveFilter - Function to change active filter (string) => void
 * @returns {Object} returns.filterMap - Complete mapping of filter names to CSS filter strings
 */
const useApplyFiltersHook = () => {
  // State for tracking the currently selected camera filter
  // Default filter is 'Normal' which applies no CSS filter
  // This state is used by components to apply the correct filter to camera feed
  const [activeFilter, setActiveFilter] = useState("Normal");

  // Return hook state and utility functions for component consumption
  // Components can destructure this object to access filter functionality
  return {
    activeFilter, // Current filter selection - used to apply CSS filter to camera
    setActiveFilter, // Function to update filter selection - called by filter UI components
    filterMap // Complete filter mapping - used for filter options and CSS application
  };
};

export default useApplyFiltersHook;
